/**
 * Unified Catalogues Browser — v8.0
 *
 * Single page that browses all reference data:
 *   - Medications (104 DCI)
 *   - Medical Devices (61 DM)
 *   - NGAP (74 actes)
 *   - Lab Drivers (10)
 *   - Imaging Drivers (13)
 *
 * With search, category filter, ATC/CE class filter.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader, KpiRow } from "@/components/PageHeader";
import { DOMAINS } from "@/lib/design-system";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { trackCenter } from "@/lib/track-center";
import {
  Pill, Package, FileCheck, FlaskConical, ScanLine, Search,
  TrendingUp, AlertTriangle, Database, Activity
} from "lucide-react";

type TabId = "medications" | "devices" | "ngap" | "lab" | "imaging";

export const Route = createFileRoute("/catalogs/")({
  component: CataloguesBrowser,
});

function CataloguesBrowser() {
  const [tab, setTab] = useState<TabId>("medications");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState<any>({});
  const [meds, setMeds] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [ngapItems, setNgap] = useState<any[]>([]);
  const [labDrivers, setLabDrivers] = useState<any[]>([]);
  const [imagingDrivers, setImagingDrivers] = useState<any[]>([]);

  useEffect(() => {
    trackCenter("operations"); // domain color: operations
    Promise.all([
      safe(() => api.referentialsStats(), "catalogs.stats").then((s: any) => setStats(s || {})),
      safe(() => api.medications({}), "catalogs.medications").then((r: any) => setMeds(r?.items || [])),
      safe(() => api.medicalDevices({}), "catalogs.devices").then((r: any) => setDevices(r?.items || [])),
      safe(() => api.ngap({}), "catalogs.ngap").then((r: any) => setNgap(r?.items || [])),
      safe(() => api.labDrivers(), "catalogs.labDrivers").then((r: any) => setLabDrivers(r?.items || [])),
      safe(() => api.imagingDrivers(), "catalogs.imagingDrivers").then((r: any) => setImagingDrivers(r?.items || [])),
    ]);
  }, []);

  const filteredMeds = useMemo(() => {
    const q = search.toLowerCase();
    return meds.filter((m) =>
      !q || m.dci.toLowerCase().includes(q) || m.commercial_name.toLowerCase().includes(q) || m.atc.toLowerCase().includes(q)
    );
  }, [meds, search]);

  const filteredDevices = useMemo(() => {
    const q = search.toLowerCase();
    return devices.filter((d) =>
      !q || d.name.toLowerCase().includes(q) || d.reference.toLowerCase().includes(q) || d.manufacturer.toLowerCase().includes(q)
    );
  }, [devices, search]);

  const filteredNgap = useMemo(() => {
    const q = search.toLowerCase();
    return ngapItems.filter((n) =>
      !q || n.label.toLowerCase().includes(q) || n.code.toLowerCase().includes(q)
    );
  }, [ngapItems, search]);

  const filteredLab = useMemo(() => {
    const q = search.toLowerCase();
    return labDrivers.filter((d) =>
      !q || d.vendor.toLowerCase().includes(q) || d.model.toLowerCase().includes(q)
    );
  }, [labDrivers, search]);

  const filteredImaging = useMemo(() => {
    const q = search.toLowerCase();
    return imagingDrivers.filter((d) =>
      !q || d.vendor.toLowerCase().includes(q) || d.model.toLowerCase().includes(q)
    );
  }, [imagingDrivers, search]);

  const topKpis = [
    { label: "Médicaments", value: stats?.medications?.total || meds.length, delta: 0, icon: Pill },
    { label: "Dispositifs médicaux", value: stats?.devices?.total || devices.length, delta: 0, icon: Package },
    { label: "Actes NGAP", value: stats?.ngap?.total || ngapItems.length, delta: 0, icon: FileCheck },
    { label: "Drivers (lab + imagerie)", value: (labDrivers.length + imagingDrivers.length), delta: 0, icon: Database },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      <PageHeader
        title="Catalogues de référence"
        description="Médicaments · Dispositifs médicaux · NGAP · Drivers lab & imagerie (Maroc)"
        domain="operations"
        actions={
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
            <Database className="h-3 w-3 mr-1" /> Source unique de vérité
          </Badge>
        }
      >
        <KpiRow items={topKpis as any} domain="operations" />
      </PageHeader>

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {([
          { id: "medications", label: "Médicaments", icon: Pill, count: meds.length },
          { id: "devices", label: "Dispositifs médicaux", icon: Package, count: devices.length },
          { id: "ngap", label: "NGAP", icon: FileCheck, count: ngapItems.length },
          { id: "lab", label: "Drivers lab", icon: FlaskConical, count: labDrivers.length },
          { id: "imaging", label: "Drivers imagerie", icon: ScanLine, count: imagingDrivers.length },
        ] as const).map((t) => {
          const Icon = t.icon;
          return (
            <Button
              key={t.id}
              variant={tab === t.id ? "default" : "outline"}
              size="sm"
              onClick={() => setTab(t.id)}
              className="gap-1"
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
              <Badge variant="outline" className="ml-1 text-[10px]">{t.count}</Badge>
            </Button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Rechercher dans le catalogue..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-slate-900 border-slate-800"
        />
      </div>

      {/* Content */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          {tab === "medications" && <MedicationsTable items={filteredMeds} />}
          {tab === "devices" && <DevicesTable items={filteredDevices} />}
          {tab === "ngap" && <NgapTable items={filteredNgap} />}
          {tab === "lab" && <LabDriversTable items={filteredLab} />}
          {tab === "imaging" && <ImagingDriversTable items={filteredImaging} />}
        </CardContent>
      </Card>
    </div>
  );
}

function MedicationsTable({ items }: { items: any[] }) {
  if (items.length === 0) return <EmptyState label="Aucun médicament" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">DCI</th>
            <th className="px-3 py-2 text-left">Nom commercial</th>
            <th className="px-3 py-2 text-left">Dosage</th>
            <th className="px-3 py-2 text-left">Forme</th>
            <th className="px-3 py-2 text-left">ATC</th>
            <th className="px-3 py-2 text-left">Liste</th>
            <th className="px-3 py-2 text-right">PPM (MAD)</th>
            <th className="px-3 py-2 text-right">CNSS %</th>
            <th className="px-3 py-2 text-left">Manufacturer</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 50).map((m) => (
            <tr key={m.id} className="border-t border-slate-800 hover:bg-slate-800/30">
              <td className="px-3 py-2 font-semibold">{m.dci}</td>
              <td className="px-3 py-2 text-slate-300">{m.commercial_name}</td>
              <td className="px-3 py-2 font-mono">{m.dosage}</td>
              <td className="px-3 py-2 text-slate-400">{m.form}</td>
              <td className="px-3 py-2 font-mono text-emerald-400">{m.atc}</td>
              <td className="px-3 py-2">
                <Badge variant="outline" className={m.controlled ? "border-red-500/40 text-red-400" : "border-slate-700"}>
                  {m.list}
                </Badge>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{m.ppm_mad}</td>
              <td className="px-3 py-2 text-right tabular-nums text-sky-400">{m.cnss_rate}%</td>
              <td className="px-3 py-2 text-slate-400 text-[10px]">{m.manufacturer}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 50 && <div className="p-3 text-center text-xs text-slate-500">+ {items.length - 50} autres résultats</div>}
    </div>
  );
}

function DevicesTable({ items }: { items: any[] }) {
  if (items.length === 0) return <EmptyState label="Aucun dispositif" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Référence</th>
            <th className="px-3 py-2 text-left">Désignation</th>
            <th className="px-3 py-2 text-left">Catégorie</th>
            <th className="px-3 py-2 text-left">CE</th>
            <th className="px-3 py-2 text-left">Fabricant</th>
            <th className="px-3 py-2 text-left">Fournisseur</th>
            <th className="px-3 py-2 text-right">Prix unit. MAD</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 50).map((d) => (
            <tr key={d.id} className="border-t border-slate-800 hover:bg-slate-800/30">
              <td className="px-3 py-2 font-mono text-violet-400">{d.reference}</td>
              <td className="px-3 py-2 font-semibold">{d.name}</td>
              <td className="px-3 py-2 text-slate-300">{d.category}</td>
              <td className="px-3 py-2">
                <Badge variant="outline" className={
                  d.ce_class === "III" ? "border-red-500/40 text-red-400" :
                  d.ce_class === "IIb" ? "border-amber-500/40 text-amber-400" :
                  "border-slate-700"
                }>{d.ce_class}</Badge>
              </td>
              <td className="px-3 py-2 text-slate-400 text-[10px]">{d.manufacturer}</td>
              <td className="px-3 py-2 text-slate-400 text-[10px]">{d.supplier}</td>
              <td className="px-3 py-2 text-right tabular-nums">{d.unit_price_mad.toLocaleString("fr-FR")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NgapTable({ items }: { items: any[] }) {
  if (items.length === 0) return <EmptyState label="Aucun acte NGAP" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Code</th>
            <th className="px-3 py-2 text-left">Libellé</th>
            <th className="px-3 py-2 text-left">Lettre</th>
            <th className="px-3 py-2 text-right">Coef</th>
            <th className="px-3 py-2 text-right">Base MAD</th>
            <th className="px-3 py-2 text-left">Modificateurs</th>
            <th className="px-3 py-2 text-left">Catégorie</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 50).map((n, i) => (
            <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/30">
              <td className="px-3 py-2 font-mono text-amber-400">{n.code}</td>
              <td className="px-3 py-2 font-semibold">{n.label}</td>
              <td className="px-3 py-2 font-mono text-violet-400">{n.letter}</td>
              <td className="px-3 py-2 text-right tabular-nums">{n.base_coef}</td>
              <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{n.base_amount_mad}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1 flex-wrap">
                  {n.modifiers?.slice(0, 3).map((m: string) => (
                    <Badge key={m} variant="outline" className="text-[10px] border-slate-700">{m}</Badge>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 text-slate-400">{n.category}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LabDriversTable({ items }: { items: any[] }) {
  if (items.length === 0) return <EmptyState label="Aucun driver lab" />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3">
      {items.map((d) => (
        <div key={d.id} className="bg-slate-950/40 p-3 rounded-lg border border-slate-800">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-[10px] border-violet-500/40 text-violet-400">
              {d.protocol}
            </Badge>
            <span className="text-[10px] text-slate-500">{d.status}</span>
          </div>
          <div className="text-sm font-semibold mt-2">{d.vendor}</div>
          <div className="text-xs text-slate-400">{d.model}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {d.capabilities?.map((c: string) => (
              <Badge key={c} variant="outline" className="text-[10px] border-slate-700">{c}</Badge>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-slate-500 font-mono">
            {d.host}:{d.port} · {d.encoding} · {(d.loinc_mapping && typeof d.loinc_mapping === "object" ? Object.keys(d.loinc_mapping) : []).length} LOINC codes
          </div>
        </div>
      ))}
    </div>
  );
}

function ImagingDriversTable({ items }: { items: any[] }) {
  if (items.length === 0) return <EmptyState label="Aucun driver imagerie" />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3">
      {items.map((d) => (
        <div key={d.id} className="bg-slate-950/40 p-3 rounded-lg border border-slate-800">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-[10px] border-cyan-500/40 text-cyan-400">
              {d.modality}
            </Badge>
            <span className="text-[10px] text-slate-500 font-mono">{d.ae_title}</span>
          </div>
          <div className="text-sm font-semibold mt-2">{d.vendor}</div>
          <div className="text-xs text-slate-400">{d.model}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {d.worklist_supported && <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400">MWL</Badge>}
            {d.mpps_supported && <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400">MPPS</Badge>}
            <Badge variant="outline" className="text-[10px] border-slate-700">DICOM</Badge>
          </div>
          <div className="mt-2 text-[10px] text-slate-500 font-mono">
            {d.host}:{d.dicom_port} · {d.sop_classes.length} SOP classes
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="p-8 text-center">
      <Database className="h-8 w-8 text-slate-600 mx-auto mb-2" />
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}